import {
  isAccessDenied,
  isPlatformJwtFresh,
  renewAfterRejection,
  renewalHasGivenUp,
} from '@/lib/sso.js';
import { setPlatformJwt } from '@/store/identity-store.js';

/**
 * What an SSE loop does when the server answers 401.
 *
 * The old answer was `setPlatformJwt(null)`, which unmounts the whole app
 * behind AuthGate and throws away everything the user had open — a brutal
 * response to one rejected request. Now the loop tries to renew first and only
 * gives up the session when renewal leaves it with nothing usable.
 *
 * `rejected` is the token the connection was refused with, and it matters: the
 * renewal has to be the kind that ignores that token's `exp`, because a 401 on
 * an unexpired token is precisely the case a freshness-gated renewal would skip
 * — leaving the loop to reconnect forever with a token the server keeps
 * refusing.
 *
 * Returns true when the caller should back off and reconnect, false when the
 * session was cleared and the loop must stop. A 401 while holding a token that
 * still looks valid, after a real renewal attempt, is not ours to fix by signing
 * the user out — that is a server-side rejection, so it is treated as any other
 * disconnect and retried under the normal backoff. So is a renewal that failed
 * for a passing reason (the network right after a laptop wakes, a slow Teams
 * client): the session ends only once renewal has given up on it.
 */
export async function recoverFrom401(
  rejected: string | null,
): Promise<boolean> {
  const renewed = await renewAfterRejection(rejected);
  // The access gate has refused this account: no reconnect can change that, so
  // the loop stops. The renewal above already ended the session — clearing it
  // again is a no-op, and stated here so the loop's own exit does not depend on
  // reading an upstream side effect.
  if (isAccessDenied()) {
    setPlatformJwt(null);
    return false;
  }
  const usable = renewed !== null && renewed.length > 0;
  if (usable && renewed !== rejected) return true;
  if (usable && isPlatformJwtFresh(renewed)) return true;
  if (usable && !renewalHasGivenUp()) return true;
  setPlatformJwt(null);
  return false;
}
